import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCashFlowProjection } from '../hooks/useCashFlow';

const COST_COLORS = [
  '#ef5350',
  '#ab47bc',
  '#26c6da',
  '#ffa726',
  '#66bb6a',
  '#42a5f5',
  '#ec407a',
  '#8d6e63',
];

const AXIS_STYLE = { fill: '#9e9e9e', fontSize: 11 };

function fmt(val: number): string {
  if (Math.abs(val) >= 1000) return `GH₵${(val / 1000).toFixed(1)}k`;
  return `GH₵${val.toFixed(0)}`;
}

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  sub?: string;
  subColor?: string;
}

function StatCard({ label, value, icon, color, sub, subColor }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: `1px solid ${color}30`,
        background: '#141414',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.5 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{ color: subColor ?? '#9e9e9e' }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.8 }}>{icon}</Box>
      </Box>
    </Paper>
  );
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  categories: string[];
}

function CustomTooltip({ active, payload, label, categories }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const revenue = payload.find((p: any) => p.dataKey === 'revenue');
  const net = payload.find((p: any) => p.dataKey === 'net');
  const isActual = payload[0]?.payload?.type === 'actual';
  return (
    <Paper sx={{ p: 1.5, minWidth: 180, background: '#1e1e1e', border: '1px solid #333' }}>
      <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block', mb: 0.5 }}>
        {label} {isActual ? '(actual)' : '(forecast)'}
      </Typography>
      {revenue && (
        <Typography variant="body2" sx={{ color: '#66bb6a' }}>
          Revenue: {fmt(revenue.value)}
        </Typography>
      )}
      {categories.map((cat, i) => {
        const p = payload.find((p: any) => p.dataKey === cat);
        if (!p) return null;
        return (
          <Typography key={cat} variant="body2" sx={{ color: COST_COLORS[i % COST_COLORS.length] }}>
            {cat}: {fmt(p.value)}
          </Typography>
        );
      })}
      {net && (
        <Typography
          variant="body2"
          sx={{ mt: 0.5, fontWeight: 700, color: net.value >= 0 ? '#66bb6a' : '#ef5350' }}
        >
          Net: {fmt(net.value)}
        </Typography>
      )}
    </Paper>
  );
}

export default function CashFlowProjection() {
  const [selectedProcurement, setSelectedProcurement] = useState<number | ''>('');

  const allData = useCashFlowProjection();
  const filteredData = useCashFlowProjection(
    selectedProcurement ? { procurement_id: selectedProcurement } : undefined
  );

  const data = selectedProcurement ? filteredData : allData;
  const procurements = useMemo(() => allData.data?.procurements ?? [], [allData.data]);

  const weeks = useMemo(() => data.data?.weeks ?? [], [data.data]);
  const categories: string[] = useMemo(() => data.data?.cost_categories ?? [], [data.data]);
  const summary = useMemo(() => data.data?.summary ?? null, [data.data]);

  const breakEvenIndex = useMemo(() => {
    if (!summary?.break_even_week) return -1;
    return weeks.findIndex((w: any) => w.week === summary.break_even_week);
  }, [weeks, summary]);

  if (data.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: '#ff9800' }} />
      </Box>
    );
  }

  if (data.isError) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        Failed to load cash flow data.
      </Alert>
    );
  }

  const projNet = summary?.projected_net_12w ?? 0;
  const projNetColor = projNet >= 0 ? '#66bb6a' : '#ef5350';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AccountBalanceWalletIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Cash Flow Projection
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
            Actuals to date + 12-week forward projection
          </Typography>
        </Box>

        {procurements.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Batch</InputLabel>
            <Select
              value={selectedProcurement}
              label="Batch"
              onChange={(e) => setSelectedProcurement(e.target.value as number | '')}
            >
              <MenuItem value="">All Batches</MenuItem>
              {procurements.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.identifier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Caveats */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon fontSize="small" />}
        sx={{
          mb: 3,
          background: 'rgba(33, 150, 243, 0.08)',
          border: '1px solid rgba(33,150,243,0.2)',
          '& .MuiAlert-message': { fontSize: 12 },
        }}
      >
        Projections assume constant lay rate ({summary?.weekly_lay_rate?.toFixed(1) ?? 0}%) and
        current price (GH₵{summary?.price_per_crate?.toFixed(2) ?? 0}/crate). Costs use last 4-week
        averages. Actual results will vary.
      </Alert>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="12-Week Projected Revenue"
            value={`GH₵${((summary?.projected_revenue_12w ?? 0) / 1000).toFixed(1)}k`}
            icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
            color="#66bb6a"
            sub={`~GH₵${((summary?.projected_revenue_12w ?? 0) / 12).toFixed(0)}/wk`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="12-Week Projected Costs"
            value={`GH₵${((summary?.projected_cost_12w ?? 0) / 1000).toFixed(1)}k`}
            icon={<TrendingDownIcon sx={{ fontSize: 28 }} />}
            color="#ef5350"
            sub={`~GH₵${((summary?.projected_cost_12w ?? 0) / 12).toFixed(0)}/wk`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Projected Net (12 wks)"
            value={`GH₵${(Math.abs(projNet) / 1000).toFixed(1)}k`}
            icon={<AccountBalanceWalletIcon sx={{ fontSize: 28 }} />}
            color={projNetColor}
            sub={projNet >= 0 ? 'Surplus' : 'Deficit'}
            subColor={projNetColor}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Break-Even"
            value={summary?.break_even_week ?? 'Within window'}
            icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
            color={summary?.break_even_week ? '#ffa726' : '#66bb6a'}
            sub={summary?.break_even_week ? 'Cumulative net turns positive' : 'Already profitable'}
            subColor={summary?.break_even_week ? '#ffa726' : '#66bb6a'}
          />
        </Grid>
      </Grid>

      {/* Main Chart */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
          Weekly Revenue vs Costs
        </Typography>
        <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
          Solid bars = actuals · Dashed divider = today · Orange line = cumulative net
        </Typography>

        {weeks.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography sx={{ color: '#9e9e9e' }}>No data available</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={420}>
            <ComposedChart data={weeks} margin={{ top: 16, right: 16, left: 8, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="week"
                tick={AXIS_STYLE}
                angle={-45}
                textAnchor="end"
                interval={Math.max(0, Math.floor(weeks.length / 14))}
              />
              <YAxis tickFormatter={fmt} tick={AXIS_STYLE} width={70} />
              <Tooltip content={<CustomTooltip categories={categories} />} />
              <Legend wrapperStyle={{ paddingTop: 8, fontSize: 12 }} />

              {/* Today divider */}
              {(() => {
                const todayIdx = weeks.findIndex((w: any) => w.type === 'forecast');
                if (todayIdx < 0) return null;
                const todayWeek = weeks[todayIdx]?.week;
                return (
                  <ReferenceLine
                    x={todayWeek}
                    stroke="#ff9800"
                    strokeDasharray="6 3"
                    label={{ value: 'Today', fill: '#ff9800', fontSize: 10, position: 'top' }}
                  />
                );
              })()}

              {/* Break-even line */}
              {breakEvenIndex >= 0 && (
                <ReferenceLine
                  x={weeks[breakEvenIndex]?.week}
                  stroke="#66bb6a"
                  strokeDasharray="4 2"
                  label={{ value: 'Break-even', fill: '#66bb6a', fontSize: 10, position: 'top' }}
                />
              )}

              {/* Revenue bar */}
              <Bar
                dataKey="revenue"
                name="Revenue"
                fill="#66bb6a"
                opacity={0.85}
                radius={[3, 3, 0, 0]}
              />

              {/* Cost category bars stacked */}
              {categories.map((cat, i) => (
                <Bar
                  key={cat}
                  dataKey={cat}
                  name={cat}
                  stackId="costs"
                  fill={COST_COLORS[i % COST_COLORS.length]}
                  opacity={0.8}
                  radius={i === categories.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                />
              ))}

              {/* Cumulative net line */}
              <Line
                dataKey="net"
                name="Weekly Net"
                stroke="#ff9800"
                strokeWidth={2}
                dot={false}
                strokeDasharray="none"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Weekly breakdown table */}
      {weeks.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Weekly Breakdown
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      color: '#9e9e9e',
                      fontWeight: 500,
                    }}
                  >
                    Week
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      color: '#9e9e9e',
                      fontWeight: 500,
                    }}
                  >
                    Type
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 12px',
                      color: '#66bb6a',
                      fontWeight: 500,
                    }}
                  >
                    Revenue
                  </th>
                  {categories.map((cat, i) => (
                    <th
                      key={cat}
                      style={{
                        textAlign: 'right',
                        padding: '8px 12px',
                        color: COST_COLORS[i % COST_COLORS.length],
                        fontWeight: 500,
                      }}
                    >
                      {cat}
                    </th>
                  ))}
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 12px',
                      color: '#ef5350',
                      fontWeight: 500,
                    }}
                  >
                    Total Cost
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '8px 12px',
                      color: '#ff9800',
                      fontWeight: 500,
                    }}
                  >
                    Net
                  </th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((row: any) => {
                  const isActual = row.type === 'actual';
                  const netColor = row.net >= 0 ? '#66bb6a' : '#ef5350';
                  return (
                    <tr
                      key={row.week}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        background: isActual ? 'transparent' : 'rgba(255,152,0,0.03)',
                      }}
                    >
                      <td style={{ padding: '7px 12px', color: '#fff' }}>{row.week}</td>
                      <td style={{ padding: '7px 12px' }}>
                        <Chip
                          label={isActual ? 'Actual' : 'Forecast'}
                          size="small"
                          sx={{
                            fontSize: 10,
                            height: 20,
                            background: isActual
                              ? 'rgba(102,187,106,0.15)'
                              : 'rgba(255,152,0,0.15)',
                            color: isActual ? '#66bb6a' : '#ff9800',
                          }}
                        />
                      </td>
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: '#66bb6a' }}>
                        {fmt(row.revenue)}
                      </td>
                      {categories.map((cat) => (
                        <td
                          key={cat}
                          style={{ padding: '7px 12px', textAlign: 'right', color: '#ccc' }}
                        >
                          {fmt(row.costs?.[cat] ?? 0)}
                        </td>
                      ))}
                      <td style={{ padding: '7px 12px', textAlign: 'right', color: '#ef5350' }}>
                        {fmt(row.total_cost)}
                      </td>
                      <td
                        style={{
                          padding: '7px 12px',
                          textAlign: 'right',
                          color: netColor,
                          fontWeight: 600,
                        }}
                      >
                        {row.net >= 0 ? '+' : ''}
                        {fmt(row.net)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
